import { gte, intersectRanges, lt, lte, rangesOverlap } from './time.js';
export function findTrack(seq, trackId) {
    return seq.tracks.find((t) => t.id === trackId);
}
export function findItem(seq, itemId) {
    for (const track of seq.tracks) {
        const index = track.items.findIndex((i) => i.id === itemId);
        if (index >= 0)
            return { track, item: track.items[index], index };
    }
    return undefined;
}
export function findItems(seq, itemIds) {
    const set = new Set(itemIds);
    const out = [];
    for (const track of seq.tracks) {
        for (const item of track.items)
            if (set.has(item.id))
                out.push(item);
    }
    return out;
}
export function itemEnd(item) {
    return item.start + item.duration;
}
/** Duração da sequência: fim do último item de qualquer track. */
export function sequenceDuration(seq) {
    let max = 0;
    for (const track of seq.tracks) {
        const last = track.items[track.items.length - 1];
        if (last)
            max = Math.max(max, itemEnd(last));
    }
    return max;
}
/** Item ativo de uma track num tempo — no máximo um, pela invariante de não-sobreposição. */
export function itemAt(track, time) {
    return track.items.find((i) => gte(time, i.start) && lt(time, itemEnd(i)));
}
export function itemsInRange(track, start, end) {
    return track.items.filter((i) => rangesOverlap(i.start, itemEnd(i), start, end));
}
/**
 * Itens visíveis/audíveis num tempo, já respeitando hidden/mute/solo e ordenados
 * de baixo para cima (ordem de composição).
 */
export function activeItemsAt(seq, time) {
    const hasSolo = seq.tracks.some((t) => t.solo);
    const out = [];
    for (const track of [...seq.tracks].sort((a, b) => a.index - b.index)) {
        if (track.hidden)
            continue;
        if (hasSolo && !track.solo)
            continue;
        const item = itemAt(track, time);
        if (item)
            out.push({ track, item });
    }
    return out;
}
/** Tempo local dentro do item (segundos desde o início do clip na timeline). */
export function localTime(item, time) {
    return time - item.start;
}
/**
 * Posição correspondente no arquivo de origem, considerando velocidade e reverse.
 * É esta função que o player usa para posicionar o `<video>`.
 */
export function sourceTimeAt(item, time) {
    if (!item.source)
        return null;
    const local = localTime(item, time);
    const offset = local * item.speed;
    return item.reverse
        ? item.source.out - offset
        : item.source.in + offset;
}
export function allItems(seq) {
    return seq.tracks.flatMap((t) => t.items);
}
export function tracksOfKind(seq, kind) {
    return seq.tracks.filter((t) => t.kind === kind);
}
/** Tracks editáveis: não travadas. Alvo padrão de operações de range. */
export function editableTracks(seq) {
    return seq.tracks.filter((t) => !t.locked);
}
/** Buracos (gaps) de uma track dentro de [0, duration]. */
export function gapsInTrack(track, until) {
    const gaps = [];
    let cursor = 0;
    for (const item of track.items) {
        if (item.start > cursor + 1e-6)
            gaps.push({ start: cursor, end: item.start });
        cursor = Math.max(cursor, itemEnd(item));
    }
    if (cursor < until - 1e-6)
        gaps.push({ start: cursor, end: until });
    return gaps;
}
/**
 * Pontos de corte candidatos a snap: bordas de clips, marcadores e 0.
 * A UI adiciona o playhead separadamente.
 */
export function snapPoints(seq, excludeItemIds = []) {
    const exclude = new Set(excludeItemIds);
    const points = new Set([0]);
    for (const track of seq.tracks) {
        for (const item of track.items) {
            if (exclude.has(item.id))
                continue;
            points.add(item.start);
            points.add(itemEnd(item));
        }
    }
    for (const marker of seq.markers)
        points.add(marker.time);
    return [...points].sort((a, b) => a - b);
}
/** Ponto de snap mais próximo dentro da tolerância, ou null. */
export function findSnap(points, time, tolerance) {
    let best = null;
    let bestDist = tolerance;
    for (const p of points) {
        const d = Math.abs(p - time);
        if (d <= bestDist) {
            bestDist = d;
            best = p;
        }
    }
    return best;
}
export function itemsOfGroup(seq, groupId) {
    return allItems(seq).filter((i) => i.groupId === groupId);
}
/** Resumo compacto da timeline — é o que vai como contexto para o LLM. */
export function summarize(seq) {
    const lines = [
        `Sequência "${seq.name}" — ${seq.width}x${seq.height} @ ${seq.frameRate.num}/${seq.frameRate.den}fps, duração ${sequenceDuration(seq).toFixed(2)}s`,
    ];
    for (const track of [...seq.tracks].sort((a, b) => b.index - a.index)) {
        const flags = [
            track.locked ? 'travada' : null,
            track.hidden ? 'oculta' : null,
            track.muted ? 'muda' : null,
        ]
            .filter(Boolean)
            .join(',');
        lines.push(`  [${track.id}] ${track.name} (${track.kind}${flags ? `, ${flags}` : ''}) — ${track.items.length} itens`);
        for (const item of track.items.slice(0, 40)) {
            const src = item.source ? ` src=${item.source.assetId}[${item.source.in.toFixed(2)}→${item.source.out.toFixed(2)}]` : '';
            const speed = item.speed !== 1 ? ` speed=${item.speed}` : '';
            lines.push(`    - ${item.id} ${item.kind} ${item.start.toFixed(2)}→${itemEnd(item).toFixed(2)}${src}${speed}`);
        }
        if (track.items.length > 40)
            lines.push(`    … +${track.items.length - 40} itens`);
    }
    return lines.join('\n');
}
/** Validação de invariantes. Retorna lista vazia quando a timeline está sã. */
export function validateSequence(seq) {
    const errors = [];
    const seenIds = new Set();
    for (const track of seq.tracks) {
        if (seenIds.has(track.id))
            errors.push(`track duplicada: ${track.id}`);
        seenIds.add(track.id);
        let prevEnd = -Infinity;
        for (const item of track.items) {
            if (seenIds.has(item.id))
                errors.push(`item duplicado: ${item.id}`);
            seenIds.add(item.id);
            if (item.start < -1e-6)
                errors.push(`${item.id}: start negativo (${item.start})`);
            if (item.duration <= 0)
                errors.push(`${item.id}: duração não-positiva (${item.duration})`);
            if (lt(item.start, prevEnd)) {
                errors.push(`${item.id}: sobrepõe o item anterior na track ${track.id}`);
            }
            if (item.source) {
                const expected = (item.source.out - item.source.in) / Math.abs(item.speed || 1);
                if (Math.abs(expected - item.duration) > 1e-3) {
                    errors.push(`${item.id}: duração ${item.duration.toFixed(4)} incoerente com source/speed (esperado ${expected.toFixed(4)})`);
                }
                if (lte(item.source.out, item.source.in)) {
                    errors.push(`${item.id}: intervalo de origem inválido`);
                }
            }
            prevEnd = itemEnd(item);
        }
    }
    return errors;
}
// ---------------------------------------------------------------------------
// Mapeamento origem ↔ timeline
// ---------------------------------------------------------------------------
/**
 * Converte um tempo do MATERIAL DE ORIGEM para o tempo na timeline.
 *
 * Este é o elo que faltava entre a transcrição e a edição. A transcrição é ancorada ao
 * arquivo original (a palavra está em 5,2s DO VÍDEO); a timeline é o resultado de cortes
 * e mudanças de velocidade. Depois de remover um silêncio, os dois deixam de coincidir —
 * e sem esta função as legendas cairiam no lugar errado e uma segunda passada de
 * remoção de silêncio tentaria cortar trechos que já não existem.
 *
 * Devolve `null` quando aquele instante foi cortado da timeline.
 */
export function sourceToTimeline(seq, assetId, sourceTime) {
    for (const track of seq.tracks) {
        for (const item of track.items) {
            const src = item.source;
            if (!src || src.assetId !== assetId)
                continue;
            if (item.reverse) {
                if (gte(sourceTime, src.in) && lt(sourceTime, src.out)) {
                    return item.start + (src.out - sourceTime) / Math.abs(item.speed || 1);
                }
                continue;
            }
            if (gte(sourceTime, src.in) && lt(sourceTime, src.out)) {
                return item.start + (sourceTime - src.in) / Math.abs(item.speed || 1);
            }
        }
    }
    return null;
}
/**
 * Mapeia um intervalo de origem para os intervalos de timeline que sobreviveram.
 *
 * Um intervalo pode virar vários: se o usuário cortou o meio de uma frase, os dois
 * pedaços que restaram estão em posições diferentes da timeline.
 */
export function sourceRangeToTimeline(seq, assetId, sourceStart, sourceEnd) {
    const out = [];
    for (const track of seq.tracks) {
        for (const item of track.items) {
            const src = item.source;
            if (!src || src.assetId !== assetId)
                continue;
            const overlap = intersectRanges(src.in, src.out, sourceStart, sourceEnd);
            if (!overlap)
                continue;
            const speed = Math.abs(item.speed || 1);
            if (item.reverse) {
                out.push({
                    start: item.start + (src.out - overlap.end) / speed,
                    end: item.start + (src.out - overlap.start) / speed,
                });
            }
            else {
                out.push({
                    start: item.start + (overlap.start - src.in) / speed,
                    end: item.start + (overlap.end - src.in) / speed,
                });
            }
        }
    }
    return out.sort((a, b) => a.start - b.start);
}
/** Ids dos assets referenciados pela timeline. */
export function referencedAssets(seq) {
    const ids = new Set();
    for (const track of seq.tracks) {
        for (const item of track.items) {
            if (item.source)
                ids.add(item.source.assetId);
        }
    }
    return [...ids];
}
/**
 * Track do tipo pedido onde o intervalo cabe sem sobrepor nada.
 *
 * Existe porque inserir da biblioteca não pode sobrescrever o que já está lá: dois
 * efeitos sonoros a meio segundo um do outro são normais, e num `ADD_ITEM` com modo
 * `overwrite` o segundo apagaria o primeiro. Quem chama usa o retorno nulo como sinal
 * de "crie mais uma track deste tipo".
 */
export function placementTrack(seq, kind, start, end) {
    return tracksOfKind(seq, kind)
        .filter((t) => !t.locked)
        .sort((a, b) => a.index - b.index)
        .find((track) => !track.items.some((item) => item.start < end && itemEnd(item) > start));
}
/**
 * Batidas e compassos que a timeline conhece.
 *
 * Os tempos chegam junto com a trilha, gravados em `item.meta` no momento da inserção,
 * e são deslocados pelo `start` do item — cortar na batida depois de arrastar a música
 * tem que acompanhar a música, não o relógio original. É por isso que ficam na timeline
 * e não no banco: depois de inserida, a trilha é um item como qualquer outro, e a
 * timeline é a fonte da verdade.
 */
export function beatGrid(seq) {
    const grid = [];
    for (const track of seq.tracks) {
        for (const item of track.items) {
            const meta = item.meta;
            const beats = Array.isArray(meta?.beats) ? meta.beats : [];
            const bars = Array.isArray(meta?.bars) ? meta.bars : [];
            if (beats.length === 0 && bars.length === 0)
                continue;
            const strongSet = new Set(bars.map((b) => Math.round(b * 1000)));
            // Compassos entram mesmo sem a lista de batidas: uma trilha que só conhece os
            // pontos fortes ainda serve para cortar no ritmo, que é o que mais importa.
            const points = [...new Set([...beats, ...bars])].sort((a, b) => a - b);
            const limit = itemEnd(item);
            for (const beat of points) {
                // `beat` é tempo dentro do arquivo; o que a timeline vê é deslocado pelo trim.
                const time = item.start + (beat - (item.source?.in ?? 0)) / (item.speed || 1);
                if (time < item.start - 1e-6 || time > limit + 1e-6)
                    continue;
                grid.push({ time, strong: strongSet.has(Math.round(beat * 1000)) });
            }
        }
    }
    return grid.sort((a, b) => a.time - b.time);
}
//# sourceMappingURL=query.js.map